import React, { useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, List,
    ListItem, ListItemText, MenuItem, Slider, TextField, Typography, Chip, Autocomplete, IconButton, Divider,
} from '@mui/material';
import { Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import {
    ActivityNode, ActivityStatus, ACTIVITY_STATUS_LABELS, ActivityUpdate, ProjectMember, Risk, Workstream,
} from '../../../types/execution';
import executionService, { ActivityPayload } from '../../../services/executionService';
import { errorMessage, formatDate } from './executionUtils';

type Notify = (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;

const STATUS_OPTIONS = Object.keys(ACTIVITY_STATUS_LABELS) as ActivityStatus[];

// ------------------------------------------------------------------ frente
interface WorkstreamDialogProps {
    open: boolean;
    projectId: number;
    workstream?: Workstream | null;
    onClose: () => void;
    onSaved: () => void;
    notify: Notify;
}

export const WorkstreamDialog: React.FC<WorkstreamDialogProps> = ({ open, projectId, workstream, onClose, onSaved, notify }) => {
    const [name, setName] = useState('');
    const [ownerId, setOwnerId] = useState('');
    const [people, setPeople] = useState<{ id: number; name: string; role: string }[]>([]);
    const [statusNote, setStatusNote] = useState('');
    const [withPhases, setWithPhases] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(workstream?.name || '');
            setOwnerId(workstream?.owner_user_id ? String(workstream.owner_user_id) : '');
            setStatusNote(workstream?.status_note || '');
            setWithPhases(true);
            executionService.listProjectPeople(projectId).then(setPeople).catch(() => setPeople([]));
        }
    }, [open, workstream, projectId]);

    const handleSave = async () => {
        try {
            setSaving(true);
            // Owner = usuário que participa do projeto (o nome exibido vem do cadastro)
            const data = { name, owner_user_id: ownerId ? Number(ownerId) : null, status_note: statusNote || null };
            if (workstream) {
                await executionService.updateWorkstream(workstream.id, data);
            } else {
                await executionService.createWorkstream(projectId, { ...data, with_default_phases: withPhases });
            }
            notify('Frente salva', 'success');
            onSaved();
            onClose();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar frente'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{workstream ? 'Editar frente' : 'Nova frente'}</DialogTitle>
            <DialogContent>
                <TextField fullWidth margin="normal" label="Nome" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
                <TextField select fullWidth margin="normal" label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
                    helperText={people.length
                        ? 'Pessoa alocada no projeto'
                        : 'Ninguém alocado ainda: aloque as pessoas nas posições (aba Equipe)'}>
                    <MenuItem value="">Sem owner</MenuItem>
                    {people.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name} · {p.role}</MenuItem>)}
                    {/* Owner antigo (texto livre) que não é usuário do projeto: mostrado para não sumir */}
                    {workstream?.owner_name && !workstream.owner_user_id && (
                        <MenuItem value="" disabled>{workstream.owner_name} (não é usuário do projeto)</MenuItem>
                    )}
                </TextField>
                <TextField
                    fullWidth margin="normal" label="Observação de status (opcional)" placeholder="Ex.: Plano em refinamento"
                    value={statusNote} onChange={(e) => setStatusNote(e.target.value)}
                    helperText="Quando preenchida, substitui o cronograma na Visão Executiva"
                />
                {!workstream && (
                    <FormControlLabel
                        control={<Checkbox checked={withPhases} onChange={(e) => setWithPhases(e.target.checked)} />}
                        label="Criar fases padrão (Definição Funcional, Definição Técnica, Desenvolvimento, UAT, Go Live)"
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={!name.trim() || saving}>Salvar</Button>
            </DialogActions>
        </Dialog>
    );
};

// --------------------------------------------------------------- atividade
interface ActivityDialogProps {
    open: boolean;
    workstream: Workstream | null;
    activity?: ActivityNode | null;
    parent?: ActivityNode | null;
    risks: Risk[];
    members?: ProjectMember[];
    onClose: () => void;
    onSaved: () => void;
    notify: Notify;
}

export const ActivityDialog: React.FC<ActivityDialogProps> = ({ open, workstream, activity, parent, risks, members = [], onClose, onSaved, notify }) => {
    const [form, setForm] = useState<ActivityPayload>({});
    const [saving, setSaving] = useState(false);
    const isLeaf = !activity || activity.children.length === 0;

    useEffect(() => {
        if (open) {
            setForm(activity ? {
                name: activity.name,
                planned_start: activity.planned_start,
                planned_end: activity.planned_end,
                actual_start: activity.actual_start,
                actual_end: activity.actual_end,
                progress: activity.progress,
                weight: activity.weight,
                status: activity.manual_status,
                milestone_label: activity.milestone_label,
                responsible_ids: activity.responsible_ids || [],
                risk_ids: activity.risk_ids,
            } : { name: '', parent_id: parent?.id ?? null, progress: 0, weight: 1, status: 'PENDING', risk_ids: [], responsible_ids: [] });
        }
    }, [open, activity, parent]);

    const set = (field: keyof ActivityPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value === '' ? null : e.target.value }));

    const handleSave = async () => {
        if (!workstream) return;
        try {
            setSaving(true);
            const payload: ActivityPayload = { ...form, weight: Number(form.weight ?? 1), progress: Number(form.progress ?? 0) };
            if (activity) {
                await executionService.updateActivity(activity.id, payload);
            } else {
                await executionService.createActivity(workstream.id, payload);
            }
            notify('Atividade salva', 'success');
            onSaved();
            onClose();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar atividade'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const projectRisks = risks.filter((r) => !r.workstream_id || r.workstream_id === workstream?.id);
    // Datas ISO (yyyy-mm-dd) comparam corretamente como texto
    const plannedError = form.planned_start && form.planned_end && form.planned_end < form.planned_start
        ? 'Fim planejado antes do início' : '';
    const actualError = form.actual_start && form.actual_end && form.actual_end < form.actual_start
        ? 'Fim real antes do início' : '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {activity ? 'Editar atividade' : parent ? `Nova atividade em "${parent.name}"` : 'Nova fase'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={8}>
                        <TextField fullWidth label="Nome" value={form.name || ''} onChange={set('name')} required autoFocus />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Rótulo no Gantt" placeholder="Ex.: Q4, TBD" value={form.milestone_label || ''} onChange={set('milestone_label')} />
                    </Grid>
                    <Grid item xs={12}>
                        {/* Responsáveis = pessoas do time do projeto ("Equipe e acessos"); pode ser mais de uma */}
                        <Autocomplete
                            multiple
                            options={members}
                            getOptionLabel={(m) => m.squad ? `${m.name} · ${m.squad}` : m.name}
                            value={members.filter((m) => (form.responsible_ids || []).includes(m.id))}
                            onChange={(_, value) => setForm((prev) => ({ ...prev, responsible_ids: value.map((m) => m.id) }))}
                            renderTags={(value, getTagProps) => value.map((m, index) => (
                                <Chip {...getTagProps({ index })} key={m.id} size="small" label={m.name} />
                            ))}
                            noOptionsText='Aloque a pessoa numa posição do projeto (aba Equipe)'
                            renderInput={(params) => <TextField {...params} label="Responsáveis"
                                helperText={members.length ? 'Pessoas alocadas no projeto' : 'Ninguém alocado ainda: aloque as pessoas nas posições (aba Equipe)'} />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth type="date" label="Início planejado" InputLabelProps={{ shrink: true }} value={form.planned_start || ''} onChange={set('planned_start')} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth type="date" label="Fim planejado" InputLabelProps={{ shrink: true }} value={form.planned_end || ''} onChange={set('planned_end')}
                            error={!!plannedError} helperText={plannedError} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth type="date" label="Início real" InputLabelProps={{ shrink: true }} value={form.actual_start || ''} onChange={set('actual_start')} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth type="date" label="Fim real" InputLabelProps={{ shrink: true }} value={form.actual_end || ''} onChange={set('actual_end')}
                            error={!!actualError} helperText={actualError} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField select fullWidth label="Estado" value={form.status || 'PENDING'} onChange={set('status')}
                            helperText="Automático = estado sugerido pelo % real x esperado">
                            {STATUS_OPTIONS.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status === 'PENDING' ? 'Automático (sugerido)' : ACTIVITY_STATUS_LABELS[status]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth type="number" label="Peso na fase" inputProps={{ min: 0, step: 0.5 }}
                            value={form.weight ?? 1} onChange={set('weight')} helperText="Peso na média ponderada do % da fase" />
                    </Grid>
                    {isLeaf && (
                        <Grid item xs={12}>
                            <Typography variant="body2" gutterBottom>% real: {Math.round(Number(form.progress ?? 0))}%</Typography>
                            <Slider
                                value={Number(form.progress ?? 0)} step={5} min={0} max={100} valueLabelDisplay="auto"
                                onChange={(_, value) => setForm((prev) => ({ ...prev, progress: value as number }))}
                            />
                        </Grid>
                    )}
                    {!isLeaf && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                                O % real e as datas desta fase são calculados a partir das atividades filhas.
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            options={projectRisks}
                            getOptionLabel={(risk) => `${risk.code} - ${risk.description}`}
                            value={projectRisks.filter((r) => (form.risk_ids || []).includes(r.id))}
                            onChange={(_, value) => setForm((prev) => ({ ...prev, risk_ids: value.map((r) => r.id) }))}
                            renderTags={(value, getTagProps) => value.map((risk, index) => (
                                <Chip {...getTagProps({ index })} key={risk.id} size="small" label={risk.code} />
                            ))}
                            renderInput={(params) => <TextField {...params} label="Riscos / bloqueios vinculados" />}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={!form.name?.trim() || !!plannedError || !!actualError || saving}>Salvar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ------------------------------------------------------ lançamento semanal
interface UpdateDialogProps {
    open: boolean;
    activity: ActivityNode | null;
    reportDate: string;
    onClose: () => void;
    onSaved: () => void;
    notify: Notify;
}

export const ActivityUpdateDialog: React.FC<UpdateDialogProps> = ({ open, activity, reportDate, onClose, onSaved, notify }) => {
    const [highlights, setHighlights] = useState('');
    const [nextSteps, setNextSteps] = useState('');
    const [status, setStatus] = useState<ActivityStatus>('IN_PROGRESS');
    const [progress, setProgress] = useState(0);
    const [history, setHistory] = useState<ActivityUpdate[]>([]);
    const [saving, setSaving] = useState(false);
    const isLeaf = !!activity && activity.children.length === 0;

    useEffect(() => {
        if (!open || !activity) return;
        const current = activity.update?.report_date === reportDate ? activity.update : null;
        // Sem lançamento na data: parte do último lançamento para facilitar a atualização
        setHighlights(current?.highlights ?? activity.update?.highlights ?? '');
        setNextSteps(current?.next_steps ?? activity.update?.next_steps ?? '');
        // Estado manual (PENDING = automático); o exibido pode ser só a sugestão do momento
        setStatus(activity.manual_status);
        setProgress(activity.progress);
        executionService.listActivityUpdates(activity.id).then(setHistory).catch(() => setHistory([]));
    }, [open, activity, reportDate]);

    const handleSave = async () => {
        if (!activity) return;
        try {
            setSaving(true);
            if (isLeaf && progress !== activity.progress) {
                await executionService.updateActivity(activity.id, { progress });
            }
            await executionService.saveActivityUpdate(activity.id, {
                report_date: reportDate, highlights, next_steps: nextSteps, status,
            });
            notify('Atualização registrada', 'success');
            onSaved();
            onClose();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar atualização'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Atualização semanal — {activity?.name}
                <Typography variant="body2" color="text.secondary">Data de reporte: {formatDate(reportDate)}</Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                        <TextField select fullWidth label="Estado" value={status} onChange={(e) => setStatus(e.target.value as ActivityStatus)}
                            helperText={status === 'PENDING'
                                ? `Automático: ${ACTIVITY_STATUS_LABELS[activity?.suggested_status || 'PENDING']} (pelo % real x esperado)`
                                : 'Estado definido manualmente'}>
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option === 'PENDING' ? 'Automático (sugerido)' : ACTIVITY_STATUS_LABELS[option]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {isLeaf && (
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2">% real: {Math.round(progress)}%</Typography>
                            <Slider value={progress} step={5} min={0} max={100} valueLabelDisplay="auto"
                                onChange={(_, value) => setProgress(value as number)} />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth multiline minRows={5} label="Highlights" value={highlights}
                            onChange={(e) => setHighlights(e.target.value)}
                            helperText="Um item por linha. Ex.: Configuração do ambiente – 21/09 OK" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth multiline minRows={5} label="Próximos passos" value={nextSteps}
                            onChange={(e) => setNextSteps(e.target.value)} helperText="Um item por linha, com data prevista" />
                    </Grid>
                </Grid>
                {history.length > 0 && (
                    <Box mt={2}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="subtitle2">Histórico</Typography>
                        <List dense>
                            {[...history].reverse().map((item) => (
                                <ListItem key={item.id} alignItems="flex-start">
                                    <ListItemText
                                        primary={`${formatDate(item.report_date)}${item.status ? ` · ${ACTIVITY_STATUS_LABELS[item.status]}` : ''}`}
                                        secondary={[item.highlights, item.next_steps && `Próximos: ${item.next_steps}`].filter(Boolean).join('\n')}
                                        secondaryTypographyProps={{ sx: { whiteSpace: 'pre-line' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>Salvar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ------------------------------------------------------- linhas de base
interface BaselineDialogProps {
    open: boolean;
    workstream: Workstream | null;
    onClose: () => void;
    onSaved: () => void;
    onView: (baselineId: number) => void;
    notify: Notify;
}

export const BaselineDialog: React.FC<BaselineDialogProps> = ({ open, workstream, onClose, onSaved, onView, notify }) => {
    const [name, setName] = useState('');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setName(workstream?.baselines.length ? `Plano ${workstream.baselines.length + 1}` : 'Plano Original');
            setReason('');
        }
    }, [open, workstream]);

    const handleCreate = async () => {
        if (!workstream) return;
        try {
            await executionService.createBaseline(workstream.id, { name, reason });
            notify('Linha de base salva. Agora você pode replanejar as datas.', 'success');
            onSaved();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar linha de base'), 'error');
        }
    };

    const handleDelete = async (baselineId: number) => {
        try {
            await executionService.deleteBaseline(baselineId);
            onSaved();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao excluir linha de base'), 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Linhas de base — {workstream?.name}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Salve uma cópia das datas planejadas antes de replanejar. Ela poderá ser incluída no relatório
                    como "plano não vigente".
                </Typography>
                <TextField fullWidth margin="normal" label="Nome" value={name} onChange={(e) => setName(e.target.value)} />
                <TextField fullWidth margin="normal" label="Motivo do replanejamento" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
                <Button variant="contained" onClick={handleCreate} disabled={!name.trim()} sx={{ mt: 1 }}>
                    Salvar datas atuais como linha de base
                </Button>
                {!!workstream?.baselines.length && (
                    <List dense sx={{ mt: 2 }}>
                        {workstream.baselines.map((baseline) => (
                            <ListItem
                                key={baseline.id}
                                secondaryAction={(
                                    <>
                                        <IconButton edge="end" onClick={() => onView(baseline.id)}><VisibilityIcon /></IconButton>
                                        <IconButton edge="end" onClick={() => handleDelete(baseline.id)}><DeleteIcon /></IconButton>
                                    </>
                                )}
                            >
                                <ListItemText primary={baseline.name} secondary={`${formatDate(baseline.created_at)}${baseline.reason ? ` · ${baseline.reason}` : ''}`} />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};
